import { useState } from "react";
import { DialogTrigger } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";

import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { Checkbox } from "@/components/generic/Checkbox";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { useLocalStorageFlag } from "@/hooks/use-local-storage-flag";
import { trialText } from "@/hooks/use-trial-period";

interface PurchaseContactDialogProps {
  contactId: string;
  isTrialPeriod: boolean;
  onPurchase: (contactId: string) => void;
  buttonText?: string;
  buttonClassName?: string;
  buttonSize?: "sm" | "lg";
  buttonIntent?: "primary" | "secondary";
}

interface FormValues {
  noShow: boolean;
}

export function PurchaseContactDialog({
  contactId,
  isTrialPeriod,
  onPurchase,
  buttonText = "Vis",
  buttonClassName = "!border-dark-theme-on-glass-variant/70 inline-flex !h-6 !min-w-0 cursor-pointer items-center gap-x-2 p-4 py-0 text-xs font-medium text-nowrap text-white",
  buttonSize = "sm",
  buttonIntent = "secondary",
}: PurchaseContactDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { isExist, setItem } = useLocalStorageFlag({
    key: "suppressPurchaseContactConfirmation",
  });
  const { control, getValues } = useForm<FormValues>({
    defaultValues: { noShow: false },
  });

  const handleButtonPress = (): void => {
    if (isExist) {
      onPurchase(contactId);
    }
  };

  return (
    <DialogTrigger onOpenChange={setIsOpen} isOpen={isOpen}>
      <Button
        onPress={handleButtonPress}
        size={buttonSize}
        intent={buttonIntent}
        animate
        className={buttonClassName}
      >
        {buttonText}
      </Button>

      {isTrialPeriod ? (
        <TrialCTAModal title={trialText} />
      ) : (
        <AlertModal
          isOpen={isOpen}
          iconDisable={true}
          title="Bekræft køb af kontaktoplysninger"
          onClose={() => setIsOpen(false)}
          onConfirm={() => {
            onPurchase(contactId);

            if (getValues("noShow")) {
              setItem();
            }
          }}
          description="Kontaktoplysninger for denne kontaktperson koster 2 credits, og vil være tilgængelig så længe at Lasso kan finde dem på hjemmesiden."
          confirmation={
            <Controller
              name="noShow"
              control={control}
              render={({ field }) => (
                <Checkbox
                  boxClassName="border-theme-outline-variant"
                  className="text-theme-on-glass-variant"
                  isSelected={field.value}
                  value="noShow"
                  onChange={field.onChange}
                >
                  Vis ikke igen
                </Checkbox>
              )}
            />
          }
        />
      )}
    </DialogTrigger>
  );
}
